import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header.jsx";
import ActionButton from "../../components/ActionButton.jsx";
import MarkdownEditor from "../../components/MarkdownEditor.jsx";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useTenant } from "../../context/TenantContext.jsx";

import { graphqlEndpoint } from "../../config.js";
import { CREATE_TENANT_ROLE } from "../../graph/tenantRoles/mutations.js";
import { READ_TENANT_ROLES_BY_TENANT } from "../../graph/tenantRoles/queries.js";
import { READ_DEPARTMENTS_BY_TENANT } from "../../graph/departments/queries.js";

const client = new GraphQLClient(graphqlEndpoint);

const RESOURCING_TYPES = [
  {
    value: "INTERNAL",
    label: "Internal",
  },
  {
    value: "EXTERNAL",
    label: "External Provider",
  },
];

const COVERAGE_STATUSES = [
  {
    value: "VACANT",
    label: "Vacant",
  },
  {
    value: "COVERED",
    label: "Covered",
  },
  {
    value: "TEMPORARILY_COVERED",
    label: "Temporarily Covered",
  },
];

const validationSchema = yup.object({
  name: yup.string().trim().required("Role name is required"),
  description: yup.string().nullable(),
  mission: yup.string().nullable(),
  departmentId: yup.string().nullable(),
  reportsToRoleId: yup.string().nullable(),
  resourcingType: yup
    .string()
    .oneOf(
      RESOURCING_TYPES.map(({ value }) => value),
      "Select a valid resourcing type"
    )
    .required("Resourcing type is required"),
  coverageStatus: yup
    .string()
    .oneOf(
      COVERAGE_STATUSES.map(({ value }) => value),
      "Select a valid coverage status"
    )
    .required("Coverage status is required"),
  externalProviderName: yup
    .string()
    .nullable()
    .test(
      "external-provider-required",
      "External provider name is required when this role is covered",
      function validateExternalProvider(value) {
        const { resourcingType, coverageStatus } = this.parent;

        const requiresProvider =
          resourcingType === "EXTERNAL" &&
          ["COVERED", "TEMPORARILY_COVERED"].includes(coverageStatus);

        if (!requiresProvider) {
          return true;
        }

        return Boolean(value?.trim());
      }
    ),
  coverageNotes: yup.string().nullable(),
});

const CreateTenantRoleForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSnackbar } = useSnackbar();
  const { tenantId, tenantName } = useTenant();

  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);

  const redirectPath =
    location.state?.redirectPath || "/manageTenantRoles";

  useEffect(() => {
    const fetchData = async () => {
      if (!tenantId) {
        return;
      }

      try {
        const [
          { readDepartmentsByTenant },
          { readTenantRolesByTenant },
        ] = await Promise.all([
          client.request(READ_DEPARTMENTS_BY_TENANT, { tenantId }),
          client.request(READ_TENANT_ROLES_BY_TENANT, { tenantId }),
        ]);

        setDepartments(readDepartmentsByTenant || []);
        setRoles(readTenantRolesByTenant || []);
      } catch (err) {
        console.error("Failed to load role form data:", err);

        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load departments and roles",
        });
      }
    };

    fetchData();
  }, [tenantId, setSnackbar]);

  const initialValues = {
    name: "",
    description: "",
    mission: "",
    departmentId: "",
    reportsToRoleId: "",
    resourcingType: "INTERNAL",
    coverageStatus: "VACANT",
    externalProviderName: "",
    coverageNotes: "",
  };

  const handleSubmit = async (values) => {
    if (!tenantId) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Select a tenant before creating a role",
      });
      return;
    }

    const input = {
      tenantId,
      name: values.name.trim(),
      description: values.description?.trim() || null,
      mission: values.mission?.trim() || null,
      departmentId: values.departmentId || null,
      reportsToRoleId: values.reportsToRoleId || null,
      resourcingType: values.resourcingType,
      coverageStatus: values.coverageStatus,
      externalProviderName:
        values.resourcingType === "EXTERNAL" &&
        values.coverageStatus !== "VACANT"
          ? values.externalProviderName?.trim() || null
          : null,
      coverageNotes: values.coverageNotes?.trim() || null,
    };

    try {
      await client.request(CREATE_TENANT_ROLE, { input });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Role created successfully",
      });

      navigate(redirectPath);
    } catch (err) {
      console.error("Failed to create role:", err);

      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to create role",
      });
    }
  };

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="CREATE ROLE"
          subtitle="Select a tenant before creating a role"
        />

        <Typography mt={3}>
          Use the tenant selector in the top bar, then return here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Header
        title="CREATE ROLE"
        subtitle={`Create role for ${tenantName || "selected tenant"}`}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                label="Role Name"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
                sx={{ gridColumn: "span 4" }}
              />

              <MarkdownEditor
                value={values.description}
                onChange={(nextValue) =>
                  setFieldValue("description", nextValue)
                }
                label="Description"
                minRows={4}
                error={Boolean(
                  touched.description && errors.description
                )}
                helperText={
                  touched.description && errors.description
                }
                sx={{ gridColumn: "span 4" }}
              />

              <MarkdownEditor
                value={values.mission}
                onChange={(nextValue) =>
                  setFieldValue("mission", nextValue)
                }
                label="Mission"
                minRows={14}
                error={Boolean(touched.mission && errors.mission)}
                helperText={touched.mission && errors.mission}
                sx={{ gridColumn: "span 4" }}
              />

              <FormControl
                fullWidth
                error={Boolean(
                  touched.departmentId && errors.departmentId
                )}
                sx={{ gridColumn: "span 2" }}
              >
                <InputLabel>Department</InputLabel>

                <Select
                  name="departmentId"
                  value={values.departmentId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Department"
                >
                  <MenuItem value="">None</MenuItem>

                  {departments.map((department) => (
                    <MenuItem
                      key={department.id}
                      value={department.id}
                    >
                      {department.name}
                    </MenuItem>
                  ))}
                </Select>

                {touched.departmentId && errors.departmentId && (
                  <FormHelperText>
                    {errors.departmentId}
                  </FormHelperText>
                )}
              </FormControl>

              <FormControl
                fullWidth
                error={Boolean(
                  touched.reportsToRoleId &&
                    errors.reportsToRoleId
                )}
                sx={{ gridColumn: "span 2" }}
              >
                <InputLabel>Reports To</InputLabel>

                <Select
                  name="reportsToRoleId"
                  value={values.reportsToRoleId}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Reports To"
                >
                  <MenuItem value="">None</MenuItem>

                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>

                {touched.reportsToRoleId &&
                  errors.reportsToRoleId && (
                    <FormHelperText>
                      {errors.reportsToRoleId}
                    </FormHelperText>
                  )}
              </FormControl>

              <FormControl
                fullWidth
                required
                error={Boolean(
                  touched.resourcingType &&
                    errors.resourcingType
                )}
                sx={{ gridColumn: "span 2" }}
              >
                <InputLabel>Resourcing Type</InputLabel>

                <Select
                  name="resourcingType"
                  value={values.resourcingType}
                  label="Resourcing Type"
                  onBlur={handleBlur}
                  onChange={(event) => {
                    const nextValue = event.target.value;

                    setFieldValue("resourcingType", nextValue);

                    if (nextValue === "INTERNAL") {
                      setFieldValue("externalProviderName", "");
                    }
                  }}
                >
                  {RESOURCING_TYPES.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>

                {touched.resourcingType &&
                  errors.resourcingType && (
                    <FormHelperText>
                      {errors.resourcingType}
                    </FormHelperText>
                  )}
              </FormControl>

              <FormControl
                fullWidth
                required
                error={Boolean(
                  touched.coverageStatus &&
                    errors.coverageStatus
                )}
                sx={{ gridColumn: "span 2" }}
              >
                <InputLabel>Coverage Status</InputLabel>

                <Select
                  name="coverageStatus"
                  value={values.coverageStatus}
                  label="Coverage Status"
                  onBlur={handleBlur}
                  onChange={(event) => {
                    const nextValue = event.target.value;

                    setFieldValue("coverageStatus", nextValue);

                    if (nextValue === "VACANT") {
                      setFieldValue("externalProviderName", "");
                    }
                  }}
                >
                  {COVERAGE_STATUSES.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>

                {touched.coverageStatus &&
                  errors.coverageStatus && (
                    <FormHelperText>
                      {errors.coverageStatus}
                    </FormHelperText>
                  )}
              </FormControl>

              {values.resourcingType === "EXTERNAL" &&
                values.coverageStatus !== "VACANT" && (
                  <TextField
                    label="External Provider Name"
                    name="externalProviderName"
                    value={values.externalProviderName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    error={Boolean(
                      touched.externalProviderName &&
                        errors.externalProviderName
                    )}
                    helperText={
                      touched.externalProviderName &&
                      errors.externalProviderName
                    }
                    sx={{ gridColumn: "span 4" }}
                  />
                )}

              <TextField
                label="Coverage Notes"
                name="coverageNotes"
                value={values.coverageNotes}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(
                  touched.coverageNotes && errors.coverageNotes
                )}
                helperText={
                  (touched.coverageNotes &&
                    errors.coverageNotes) ||
                  "Add context about vacancies, temporary cover or future resourcing."
                }
                multiline
                minRows={3}
                sx={{ gridColumn: "span 4" }}
              />
            </Box>

            <Box
              display="flex"
              justifyContent="flex-end"
              mt="20px"
            >
              <ActionButton
                type="submit"
                tone="success"
                disabled={isSubmitting}
              >
                Create Role
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateTenantRoleForm;